import React, { useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { Building2, Search, X, DoorOpen, MapPin } from 'lucide-react';
import { differenceInCalendarDays, parseISO, format, isValid } from 'date-fns';

// Availability filter options — the core knob Sales asked for.
const AVAILABILITY_FILTERS = [
  { value: 'now', label: 'Available now' },
  { value: '30', label: 'Free up in ≤30 days' },
  { value: '60', label: 'Free up in ≤60 days' },
  { value: '90', label: 'Free up in ≤90 days' },
  { value: '180', label: 'Free up in ≤6 months' },
  { value: '365', label: 'Free up in ≤12 months' },
  { value: 'all', label: 'All (available + upcoming)' },
];

const CATEGORY_FILTERS = [
  { value: 'all', label: 'MO + CW' },
  { value: 'MO', label: 'MO only' },
  { value: 'CW', label: 'CW only' },
];

const safeParse = (s) => {
  if (!s) return null;
  try {
    const d = typeof s === 'string' && s.includes('T') ? new Date(s) : parseISO(s);
    return isValid(d) ? d : null;
  } catch { return null; }
};

const daysBadge = (days) => {
  if (days == null) return null;
  if (days < 0) return { label: 'Expired', tone: 'red' };
  if (days <= 15) return { label: `${days}d`, tone: 'red' };
  if (days <= 30) return { label: `${days}d`, tone: 'amber' };
  if (days <= 90) return { label: `${days}d`, tone: 'orange' };
  return { label: `${days}d`, tone: 'muted' };
};

const toneClasses = (tone) => ({
  red: 'bg-red-500/10 text-red-600 border-red-500/30',
  amber: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  orange: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
  emerald: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  muted: 'bg-muted/40 text-muted-foreground border-border',
}[tone] || 'bg-muted/40 text-muted-foreground border-border');

/**
 * Cross-centre inventory view for Sales.
 *
 * Combines all units across every venue into one filterable list. The core
 * filter is "availability" — either currently Available OR expected to free
 * up within a window. Additional filters: category (MO/CW), min seats, free
 * text search on unit name / client / venue name.
 */
export const CrossCentreView = ({ units, venues = [] }) => {
  const [availability, setAvailability] = useState('60');
  const [category, setCategory] = useState('all');
  const [minSeats, setMinSeats] = useState('');
  const [search, setSearch] = useState('');
  const [now] = useState(() => new Date());

  const filtered = useMemo(() => {
    const rows = [];
    for (const u of units) {
      // Category
      if (category !== 'all' && u.category !== category) continue;
      // Min seats
      if (minSeats && (u.total_seats || 0) < parseInt(minSeats)) continue;
      // Search
      if (search.trim()) {
        const s = search.trim().toLowerCase();
        const hay = [u.name, u.client_name, u.venue_name, u.venue_city]
          .filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(s)) continue;
      }
      // Availability
      const isAvailable = u.status !== 'sold';
      const expiry = safeParse(u.lockin_expiry_date);
      const days = expiry ? differenceInCalendarDays(expiry, now) : null;
      let include = false;
      if (availability === 'now') {
        include = isAvailable;
      } else if (availability === 'all') {
        include = true;
      } else {
        // Sold expiring in ≤N days OR already available
        const cap = parseInt(availability);
        if (isAvailable) include = true;
        else if (days != null && days >= 0 && days <= cap) include = true;
      }
      if (!include) continue;
      rows.push({ ...u, _days: days, _expiry: expiry, _available: isAvailable });
    }
    // Sort: available first, then by expiry ascending
    rows.sort((a, b) => {
      if (a._available !== b._available) return a._available ? -1 : 1;
      if (a._expiry && b._expiry) return a._expiry - b._expiry;
      return a.name.localeCompare(b.name);
    });
    return rows;
  }, [units, availability, category, minSeats, search, now]);

  const floorOrderByVenue = useMemo(() => {
    const m = new Map();
    for (const v of venues) m.set(v.id, v.floors || []);
    return m;
  }, [venues]);

  // Group rows by venue, then by floor within each venue, for readability.
  const grouped = useMemo(() => {
    const g = new Map();
    for (const r of filtered) {
      const key = r.venue_id || 'unknown';
      if (!g.has(key)) g.set(key, { venue_name: r.venue_name || 'Unknown venue', venue_city: r.venue_city, units: [] });
      g.get(key).units.push(r);
    }
    const venueGroups = Array.from(g.entries()).map(([venueId, v]) => {
      const floorOrder = floorOrderByVenue.get(venueId) || [];
      const byFloor = new Map();
      for (const u of v.units) {
        const key = u.floor || '__unassigned__';
        if (!byFloor.has(key)) byFloor.set(key, []);
        byFloor.get(key).push(u);
      }
      const orderedKeys = floorOrder.filter(f => byFloor.has(f));
      const extra = [...byFloor.keys()].filter(k => k !== '__unassigned__' && !floorOrder.includes(k)).sort();
      orderedKeys.push(...extra);
      if (byFloor.has('__unassigned__')) orderedKeys.push('__unassigned__');
      return {
        ...v,
        floorGroups: orderedKeys.map(key => ({ floor: key === '__unassigned__' ? null : key, units: byFloor.get(key) })),
      };
    });
    return venueGroups.sort((a, b) => (a.venue_name || '').localeCompare(b.venue_name || ''));
  }, [filtered, floorOrderByVenue]);

  const availableCount = filtered.filter(r => r._available).length;
  const upcomingCount = filtered.length - availableCount;

  return (
    <div className="space-y-4" data-testid="cross-centre-view">
      {/* Filter bar */}
      <div className="p-4 rounded-lg border border-border bg-card/40 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Availability</label>
            <Select value={availability} onValueChange={setAvailability}>
              <SelectTrigger className="mt-1" data-testid="cc-availability-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AVAILABILITY_FILTERS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" data-testid="cc-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORY_FILTERS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Min seats</label>
            <Input type="number" min={0} value={minSeats} onChange={(e) => setMinSeats(e.target.value)} placeholder="e.g. 10" className="mt-1" data-testid="cc-min-seats" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Search</label>
            <div className="relative mt-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Unit / client / centre" className="pl-8 pr-7" data-testid="cc-search" />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-2.5">
                  <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <Badge className={toneClasses('emerald')}>{availableCount} available now</Badge>
          <Badge className={toneClasses('orange')}>{upcomingCount} upcoming</Badge>
          <span className="text-muted-foreground ml-auto">{grouped.length} centre{grouped.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Nothing matches these filters. Widen availability window or clear the search.
        </Card>
      ) : (
        <div className="space-y-4" data-testid="cc-results">
          {grouped.map(g => (
            <div key={g.venue_name} className="space-y-3" data-testid={`cc-venue-group-${g.venue_name}`}>
              <div className="flex items-baseline gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <h4 className="font-heading font-semibold text-sm">{g.venue_name}</h4>
                {g.venue_city && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{g.venue_city}</span>}
                <span className="text-xs text-muted-foreground ml-auto">{g.units.length} unit{g.units.length !== 1 ? 's' : ''}</span>
              </div>
              {g.floorGroups.map(fg => (
                <div key={fg.floor || 'unassigned'} className="space-y-2 pl-2">
                  {g.floorGroups.length > 1 && (
                    <p className="text-xs font-medium text-muted-foreground">{fg.floor || 'Unassigned floor'} · {fg.units.length} unit{fg.units.length !== 1 ? 's' : ''}</p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {fg.units.map(u => <CrossCentreCard key={u.id} unit={u} />)}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CrossCentreCard = ({ unit }) => {
  const badge = unit._available
    ? { label: 'Available', tone: 'emerald' }
    : daysBadge(unit._days) || { label: 'Sold', tone: 'red' };
  return (
    <Card className="p-3 space-y-2" data-testid={`cc-card-${unit.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading font-semibold text-sm truncate">{unit.name}</span>
            <Badge variant="outline" className="text-[10px]">{unit.category}</Badge>
            {unit.floor && <Badge variant="secondary" className="text-[10px]">{unit.floor}</Badge>}
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {unit.total_seats} seats · {(unit.components || []).map(c => c.seats).join('/')}
          </p>
        </div>
        {unit._available ? (
          <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${toneClasses(badge.tone)}`}>
            <DoorOpen className="w-3 h-3" /> {badge.label}
          </span>
        ) : (
          <Popover>
            <PopoverTrigger asChild>
              <button className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${toneClasses(badge.tone)}`}>
                {badge.label}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64 text-xs space-y-1">
              <p><span className="text-muted-foreground">Client: </span><b>{unit.client_name || '—'}</b></p>
              <p><span className="text-muted-foreground">Frees up: </span><b>{unit._expiry ? format(unit._expiry, 'PPP') : '—'}</b></p>
              {unit.client_note && <p className="text-muted-foreground italic">&ldquo;{unit.client_note}&rdquo;</p>}
            </PopoverContent>
          </Popover>
        )}
      </div>
    </Card>
  );
};
